import { doc, serverTimestamp } from 'firebase/firestore';
import { RoleOption } from 'shared/types/roles';
import { NewSubTask, NewTask, Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';

import { dateToFirestoreTimestamp } from './converters/date-to-firestore-timestamp';
import { newTaskConverter } from './converters/task';
import {
  BatchDocuments,
  CollectionOptions,
  getAuthentication,
  getCollection,
  getDatabase,
} from './helpers';

export type TaskEntry = {
  task: NewTask;
  subTasks: NewSubTask[];
};

export const createTasksWithSubTasks = async (
  taskEntries: TaskEntry[],
): Promise<Task[]> => {
  const user = (await getAuthentication()).currentUser;

  if (!user) {
    throw Error('unauthenticated');
  }

  const taskCollection = getCollection(CollectionOptions.Tasks);
  const batchDocuments = new BatchDocuments(getDatabase());
  const createdTasks: Task[] = [];

  for (const entry of taskEntries) {
    const parentDocRef = doc(taskCollection);
    const mainTask: Task = {
      ...dateToFirestoreTimestamp({
        ...newTaskConverter(entry.task),
        id: parentDocRef.id,
      }),
      updatedAt: serverTimestamp() as unknown as Timestamp,
      roles: { [RoleOption.Owner]: user.uid, [RoleOption.All]: [user.uid] },
    };

    if (entry.subTasks.length) {
      const subTasksWithIds = entry.subTasks.map((subTask) => {
        const docRef = doc(taskCollection);
        return {
          task: {
            ...dateToFirestoreTimestamp({
              ...newTaskConverter(subTask as unknown as NewTask),
              id: docRef.id,
            }),
            updatedAt: serverTimestamp() as unknown as Timestamp,
            parentIds: [parentDocRef.id],
            roles: {
              [RoleOption.Owner]: user.uid,
              [RoleOption.All]: [user.uid],
            },
          },
          docRef,
        };
      });

      mainTask.childIds = subTasksWithIds.map(({ docRef }) => docRef.id);

      subTasksWithIds.forEach(({ task, docRef }) => {
        batchDocuments.addDocument({ data: task, ref: docRef });
      });
    }

    batchDocuments.addDocument({ data: mainTask, ref: parentDocRef });
    createdTasks.push(mainTask);
  }

  batchDocuments.commit();

  return createdTasks;
};
